import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { Button, Form, Input, message, Modal, ModalProps, Popover } from 'antd';
import FormField from 'components/FormField';
import WhatsAppTemplatePreview from 'components/WhatsAppTemplatePreview';
import { Formik, FormikConfig } from 'formik';
import { useMakeConversationCache } from 'hooks/business/conversation';
import useIM from 'hooks/business/im/useIM';
import useModal from 'hooks/useModal';
import { useMemo } from 'react';
import { getTalkInfo } from 'services/api/conversation.convList';
import { getTemplateList } from 'views/Aide/Whatsapp/service';
import { regionLangMap, regionPhoneMap } from 'views/Aide/Whatsapp/share';
import { urlConceptReg } from 'views/Aide/Whatsapp/WhatsAppEditor/ButtonEditor';
import { getLength, value2editorValue } from 'views/Aide/Whatsapp/WhatsAppEditor/ContentEditor';

export function WhatsAppTemplateListComponent({
  templateList,
  conversation: { conversationId },
  onSend,
  onSendClick,
}: {
  templateList: (Aide.WhatsAppTemplate & { name: string })[];
  conversation: Conversation.Conversation;
  onSend?: () => void;
  onSendClick?: () => void;
}) {
  const { t } = useTranslate();

  const { sendTemplate } = useIM();

  const { visible, show, hide, meta } = useModal<ConceptModalProps>();

  const setConversation = useMakeConversationCache('set');

  return (
    <Content>
      {templateList.map((template) => {
        const { name, lang, header, content, translateContent, footer, buttons } = template;
        const sendContent = getLength(translateContent) ? translateContent : content;
        return (
          <li>
            <div>{name}</div>
            <span>
              <Popover
                content={
                  <WhatsAppTemplatePreview
                    style={{
                      width: '242px',
                      margin: '-12px -16px',
                    }}
                    template={{
                      header,
                      contents: value2editorValue(sendContent),
                      footer,
                      buttons,
                    }}
                    assets={{
                      pdfTitleText: t(
                        'scene.chatWorkspace.messageEditor.whatsAppTemplate.pdfThumbnail',
                      ),
                      pdfNameText: t('scene.chatWorkspace.messageEditor.whatsAppTemplate.fileName'),
                      contentPlaceholder: t(
                        'scene.chatWorkspace.messageEditor.whatsAppTemplate.previewPlaceholder',
                      ),
                    }}
                  />
                }
              >
                <Button size="small">{t('common.action.preview')}</Button>
              </Popover>
              <Button
                ghost
                size="small"
                type="primary"
                onClick={() => {
                  onSendClick?.();
                  const { headerConcepts, normalConcepts, buttonConcepts } = getConcepts(template);
                  return (
                    normalConcepts.length || buttonConcepts.length
                      ? new Promise<Concept[]>((resolve, reject) => {
                          show({
                            title: name,
                            onCancel: () => {
                              hide();
                              reject();
                            },
                            initialValues: [...normalConcepts, ...buttonConcepts],
                            onSubmit: (values) => {
                              resolve([...values, ...headerConcepts]);
                              hide();
                              return Promise.resolve();
                            },
                          });
                        })
                      : Promise.resolve(headerConcepts)
                  )
                    .then((concepts) => {
                      return sendTemplate(conversationId, {
                        name,
                        language: lang,
                        text: sendContent.reduce((res, cur) => {
                          if (cur.type === 'CONCEPT') {
                            return (
                              res +
                              (concepts.find(
                                (concept) =>
                                  concept.type === 'normal' && concept.name === cur.concept,
                              )?.content || '-')
                            );
                          }
                          return res + cur.text;
                        }, ''),
                        parameters: concepts.map((concept) => {
                          if (concept.type === 'web') {
                            return {
                              label: 'Website',
                              content: concept.content,
                            };
                          }
                          if (concept.type === 'file') {
                            return {
                              label: 'File',
                              content: concept.content,
                            };
                          }
                          return {
                            label: concept.name,
                            content: concept.content,
                          };
                        }),
                      });
                    })
                    .then((success) => {
                      if (success) {
                        getTalkInfo({ conversationId }).then((res) => setConversation(res));
                        onSend?.();
                      }
                    })
                    .catch(() => {});
                }}
              >
                {t('common.action.send')}
              </Button>
            </span>
          </li>
        );
      })}
      {meta && (
        <ConceptModal
          key={meta?.title?.toString()}
          visible={visible}
          onCancel={() => hide()}
          {...meta}
        />
      )}
    </Content>
  );
}

function WhatsAppTemplateList({
  conversation,
  onSend,
}: {
  conversation: Conversation.Conversation;
  onSend?: () => void;
}) {
  const { t } = useTranslate();

  const { data } = useRequest(() => getTemplateList().then(({ details }) => details), {
    onError: () => message.error(t('common.feedback.fetchFailed')),
  });

  const { externalBuyerId } = conversation;

  const templateList = useMemo(() => {
    const targetLang =
      regionLangMap[
        Object.keys(regionPhoneMap).find((region) => {
          return externalBuyerId.startsWith(`${regionPhoneMap[region]}`);
        }) || 'SG'
      ];
    return (
      data?.reduce<any[]>((res, { name, template, show: display, status }) => {
        if (!display || !status) {
          return res;
        }
        return template.reduce((res, template) => {
          const { lang, state } = template;
          if (lang !== targetLang || state !== 'APPROVED') {
            return res;
          }
          res.push({
            ...template,
            name,
          });
          return res;
        }, res);
      }, []) || []
    );
  }, [data, externalBuyerId]);

  return (
    <Container>
      <div>{t('scene.chatWorkspace.whatsAppTemplate.hint.templateOnly')}</div>
      <WhatsAppTemplateListComponent
        templateList={templateList}
        conversation={conversation}
        onSend={onSend}
      />
    </Container>
  );
}

interface NormalConcept {
  type: 'normal';
  name: string;
  content: string;
}

interface WebConcept {
  type: 'web';
  example: string;
  content: string;
}

interface FileConcept {
  type: 'file';
  content: string;
}

type Concept = NormalConcept | WebConcept | FileConcept;

function getConcepts(template: Aide.WhatsAppTemplate) {
  const { content, translateContent, buttons, header } = template;

  const sendContent = getLength(translateContent) ? translateContent : content;

  const normalConcepts = sendContent.reduce<NormalConcept[]>((res, cur) => {
    if (cur.type === 'CONCEPT') {
      res.push({
        type: 'normal',
        name: cur.concept,
        content: '',
      });
    }
    return res;
  }, []);

  const buttonConcepts = buttons.reduce<WebConcept[]>((res, cur) => {
    if (cur.type === 'ACTIVITY' && cur.optionType === 'URL' && cur.example) {
      res.push({
        type: 'web',
        example: cur.url.replace(urlConceptReg, ''),
        content: '',
      });
    }
    return res;
  }, []);

  const getVideoUrl = (url: string) => {
    const { origin, pathname } = new URL(url);
    return `${origin}${pathname}`;
  };

  const headerConcepts: FileConcept[] =
    header?.type && header.type !== 'text'
      ? [
          {
            type: 'file',
            content: header.type === 'video' ? getVideoUrl(header.previewUrl) : header.previewUrl,
          },
        ]
      : [];

  return {
    headerConcepts,
    normalConcepts,
    buttonConcepts,
  };
}

type ConceptModalProps = ModalProps & Pick<FormikConfig<Concept[]>, 'initialValues' | 'onSubmit'>;

function ConceptModal({ initialValues, onSubmit, onCancel, ...otherProps }: ConceptModalProps) {
  const { t } = useTranslate();

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
      {({ values, isSubmitting, handleSubmit, resetForm }) => {
        return (
          <Modal
            width={480}
            okText={t('scene.chatWorkspace.messageEditor.whatsAppTemplate.action.confirmSend')}
            okButtonProps={{
              loading: isSubmitting,
              onClick: () => handleSubmit(),
            }}
            onCancel={(e) => {
              onCancel?.(e);
              resetForm();
            }}
            {...otherProps}
          >
            <Form layout="vertical">
              {values.map((value, index) => {
                if (value.type === 'normal') {
                  return (
                    <FormField<string> name={`${index}.content`} required label={value.name}>
                      {({ field }) => {
                        return (
                          <Input
                            showCount
                            maxLength={80}
                            placeholder={t('common.placeholder.inputParam', {
                              x: t('scene.chatWorkspace.whatsAppTemplate.field.variableContent'),
                            })}
                            {...field}
                          />
                        );
                      }}
                    </FormField>
                  );
                }
                if (value.type === 'web') {
                  return (
                    <FormField<string>
                      name={`${index}.content`}
                      required
                      label={t('domain.whatsAppTemplate.type.website')}
                    >
                      {({ field }) => {
                        return (
                          <Web>
                            <div>{value.example}</div>
                            <Input
                              showCount
                              maxLength={80}
                              placeholder={t('common.placeholder.inputParam', {
                                x: t('domain.whatsAppTemplate.type.website'),
                              })}
                              {...field}
                            />
                          </Web>
                        );
                      }}
                    </FormField>
                  );
                }
                return null;
              })}
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  > div {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    height: 40px;
    margin: 0 100px;
  }
  > ul {
    flex: 1 0 0;
    align-self: stretch;
  }
`;

const Content = styled.ul`
  margin: 0;
  padding: 8px 12px;
  list-style: none;
  overflow: auto;
  > li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding: 4px 8px;
    border: solid 1px #e1e4eb;
    border-radius: 2px;
    background: #fff;
    > div {
      flex: 1 0 0;
      text-align: left;
      overflow: hidden;
      white-space: pre-wrap;
    }
    > span {
      flex: 0 0 auto;
      > button {
        margin-left: 10px;
      }
    }
  }
`;

const Web = styled.div`
  display: flex;
  > div {
    height: 32px;
    line-height: 32px;
    margin-right: 6px;
  }
`;

export default WhatsAppTemplateList;
